import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  Voeging,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, filter, Observable, shareReplay, tap} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-fiscale-eenheid-voeging',
  templateUrl: './fiscale-eenheid-voeging.component.html',
  styleUrls: ['./fiscale-eenheid-voeging.component.scss'],
  imports: [
    CommonModule,
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule
  ]
})
export class FiscaleEenheidVoegingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  public dialogVisible = false;

  public voegingFg = new FormGroup({
    voeging: new FormArray<FormGroup>([])
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (!data.aangifte) {
          return;
        }
        this.updateForm(data.aangifte);
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  showDialog(): void {
    this.dialogVisible = true;
  }

  get voeging(): FormArray {
    return this.voegingFg.get('voeging') as FormArray;
  }

  addRij(): void {
    this.voeging.push(this.createFormGroup());
  }

  verwijderRij(index: number): void {
    this.voeging.removeAt(index);
    this.saveForm();
  }

  createFormGroup(item?: Voeging): FormGroup {
    return new FormGroup({
      naamDochtervennootschap: new FormControl<string | null>(item?.naamDochtervennootschap ?? null),
      waarderingSchuldBalansDochter: new FormControl<number | null>(item?.waarderingSchuldBalansDochter ?? null),
      waarderingVorderingBalansAndereMaatschappij: new FormControl<number | null>(item?.waarderingVorderingBalansAndereMaatschappij ?? null),
      belasteVrijvalSchuldBijDochter: new FormControl<number | null>(item?.belasteVrijvalSchuldBijDochter ?? null),
      waarderingSchuldOpBalansAndereMaatschappij: new FormControl<number | null>(item?.waarderingSchuldOpBalansAndereMaatschappij ?? null),
      waarderingVorderingOpBalansDochter: new FormControl<number | null>(item?.waarderingVorderingOpBalansDochter ?? null),
      belasteVrijvalSchuldAndereMaatschappij: new FormControl<number | null>(item?.belasteVrijvalSchuldAndereMaatschappij ?? null),
      wevDeelnemingInDochter: new FormControl<number | null>(item?.wevDeelnemingInDochter ?? null),
      boekwaardeDeelnemingInDochter: new FormControl<number | null>(item?.boekwaardeDeelnemingInDochter ?? null),
      fiscaalVermogenDochter: new FormControl<number | null>(item?.fiscaalVermogenDochter ?? null),
      herwaarderingDochterDoorMoeder: new FormControl<number | null>(item?.herwaarderingDochterDoorMoeder ?? null),
      vermogenssprongMoederBijVoeging: new FormControl<number | null>(item?.vermogenssprongMoederBijVoeging ?? null),
    });
  }

  ngOnInit(): void {
    this.voegingFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  private updateForm(aangifte: VpbAangifte): void {
    const lijst = aangifte.specificatieFiscaleEenheid?.specificatieVoeging ?? [];
    const formArray = this.voeging;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(lijst[i], {emitEvent: false});
      } else {
        formArray.push(this.createFormGroup(lijst[i]), {emitEvent: false});
      }
    }
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.voegingFg.updateValueAndValidity({emitEvent: false});

    const specificatieVoeging: Voeging[] = this.voeging.getRawValue()
      .filter((row: any) => row.naamDochtervennootschap)
      .map((row: any): Voeging => ({
        naamDochtervennootschap: row.naamDochtervennootschap ?? undefined,
        waarderingSchuldBalansDochter: row.waarderingSchuldBalansDochter ?? undefined,
        waarderingVorderingBalansAndereMaatschappij: row.waarderingVorderingBalansAndereMaatschappij ?? undefined,
        belasteVrijvalSchuldBijDochter: row.belasteVrijvalSchuldBijDochter ?? undefined,
        waarderingSchuldOpBalansAndereMaatschappij: row.waarderingSchuldOpBalansAndereMaatschappij ?? undefined,
        waarderingVorderingOpBalansDochter: row.waarderingVorderingOpBalansDochter ?? undefined,
        belasteVrijvalSchuldAndereMaatschappij: row.belasteVrijvalSchuldAndereMaatschappij ?? undefined,
        wevDeelnemingInDochter: row.wevDeelnemingInDochter ?? undefined,
        boekwaardeDeelnemingInDochter: row.boekwaardeDeelnemingInDochter ?? undefined,
        fiscaalVermogenDochter: row.fiscaalVermogenDochter ?? undefined,
        herwaarderingDochterDoorMoeder: row.herwaarderingDochterDoorMoeder ?? undefined,
        vermogenssprongMoederBijVoeging: row.vermogenssprongMoederBijVoeging ?? undefined,
      }));

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieFiscaleEenheid: {
            ...currentEntity.aangifte.specificatieFiscaleEenheid,
            specificatieVoeging,
          },
        } as VpbAangifte,
      };
    });
  }
}
